import type { ChannelSlice } from '../ChannelSlice';
import { NEW_TABLE_RESET } from '../TableModel';
import type { TableConstructionProgress } from '../TableModel';
import type { WidgetHeaderElement } from '../WidgetModel';
import { COMMON_HEADER_ELEMENTS, STANDALONE_COMMON_HEADER_ELEMENTS } from '../WidgetModel/impl';
import type { WidgetTabSlice } from '../WidgetTabSlice';
import type { NewsWidgetModel, NewsWidgetModelEventBus, NewsWidgetViewDetails } from './index';
import { Duration } from '@js-joda/core';
import { computed, effect, signal } from '@preact/signals-react';
import type { ReadonlySignal, Signal } from '@preact/signals-react';
import { hashKey } from '@tanstack/query-core';
import type { QueryObserverOptions } from '@tanstack/query-core';
import { Slugs } from '@thinkalpha/common/constants/slugs.js';
import { notNullOrUndefined } from '@thinkalpha/common/util/notNullOrUndefined.js';
import type { ConcreteIndicator } from '@thinkalpha/platform-ws-client/contracts/dictionary.js';
import { RowIdField } from '@thinkalpha/table-client';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import { optional } from 'inversify';
import type { TableStatus } from 'src/components/table-view/model';
import type { IdeaDTO } from 'src/dtos/Idea';
import { SearchPlanDTO } from 'src/dtos/Idea/SearchPlanDTO';
import { ReactiveInjectable, reacts, inject, injectable } from 'src/features/ioc';
import { newsGridColumns } from 'src/features/news-list/grid/newsGridColumns';
import { createNewsList } from 'src/features/news-list/model';
import type { NewsTimeList } from 'src/features/news-list/newsAdapter';
import { ScannerPlanDTO } from 'src/features/scanner/dtos/ScannerPlanDTO';
import type { ScreenerPlanDTO } from 'src/features/screener/dtos/ScreenerPlanDTO';
import type { InstrumentType } from 'src/features/symbol-picker/state';
import { ideaDTOToStage } from 'src/features/table/lib/ideaStages';
import { TimeListColumns } from 'src/features/time-list-grid/TimeListColumns';
import type { WatchlistPlanDTO } from 'src/features/watchlist/dtos/WatchlistPlanDTO';
import { connectReducerDevtools } from 'src/lib/devtools';
import type { ReducerDevtoolsConnection } from 'src/lib/devtools';
import { enableNewsListDebugging } from 'src/lib/featureFlags/flags/enableNewsListDebugging';
import { createTable } from 'src/lib/table';
import { FilterStage, OutputStage, SourceStage, UnionStage } from 'src/lib/tableStages';
import type { Stage } from 'src/lib/tableStages';
import type { NewsPersistedState, NewsWidgetAdapter } from 'src/models/NewsWidgetAdapter';
import { getIdeaDTOByIdQuery } from 'src/queries/ideas';
import type { WorkspaceService } from 'src/services/WorkspaceService';
import type { FetchableTableCreationResultWithColumns } from 'src/types';
import type { ReactBindings } from 'src/types/bindings';
import type { ConnectionStatusSignal } from 'src/types/EventBus';
import { EventBusBuilder, MergedEventBus } from 'src/types/EventBus/EventBus';
import type { SlowIdeaSource } from 'src/types/slowIdeaSource';
import { v4 } from 'uuid';

// Default duration a symbol remains in the news list after being removed from the table
const DEFAULT_SYMBOL_RETENTION = Duration.ofSeconds(0);

type NewsWidgetModelAction =
    | {
          type: 'INITIALIZE_SUCCESS';
          changeCloseSlug: Signal<ConcreteIndicator>;
          symbolSlug: Signal<ConcreteIndicator>;
          universeId: ReadonlySignal<string | null>;
          symbolFilter: string | null;
          symbolRetention: Duration;
      }
    | { type: 'UPDATE_FAST_IDEAS'; ideas: (IdeaDTO<ScannerPlanDTO> | IdeaDTO<SearchPlanDTO>)[] }
    | {
          type: 'UPDATE_SLOW_IDEAS';
          ideas: (IdeaDTO<ScreenerPlanDTO> | IdeaDTO<WatchlistPlanDTO> | { universeId: string })[];
      }
    | { type: 'UPDATE_SYMBOL_FILTER'; symbol: string | null }
    | { type: 'UPDATE_SYMBOL_RETENTION'; duration: Duration }
    | { type: 'SYMBOLS_SNAPSHOT_RECEIVED'; symbols: Iterable<string> }
    | { type: 'SYMBOLS_UPDATE_RECEIVED'; added: Iterable<string>; removed: Iterable<string> }
    | { type: 'RESET' };

type NewsWidgetModelState =
    | { type: 'initializing' }
    | {
          type: 'waiting-for-symbols';
          instanceId: string;
          changeCloseSlug: Signal<ConcreteIndicator>;
          symbolSlug: Signal<ConcreteIndicator>;
          universeId: ReadonlySignal<string | null>;
          fastIdeas: Signal<(IdeaDTO<ScannerPlanDTO> | IdeaDTO<SearchPlanDTO>)[]>;
          symbolFilter: Signal<string | null>;
          symbolRetention: Signal<Duration>;
      }
    | {
          type: 'ready';
          instanceId: string;
          changeCloseSlug: Signal<ConcreteIndicator>;
          symbolSlug: Signal<ConcreteIndicator>;
          universeId: ReadonlySignal<string | null>;
          fastIdeas: Signal<(IdeaDTO<ScannerPlanDTO> | IdeaDTO<SearchPlanDTO>)[]>;
          symbolFilter: Signal<string | null>;
          symbolRetention: Signal<Duration>;
          symbols: Signal<Set<string>>;
      };

export function newsWidgetModelReducer(
    state: NewsWidgetModelState,
    action: NewsWidgetModelAction,
): NewsWidgetModelState {
    switch (action.type) {
        case 'INITIALIZE_SUCCESS':
            return {
                type: 'waiting-for-symbols',
                instanceId: v4(),
                changeCloseSlug: action.changeCloseSlug,
                symbolSlug: action.symbolSlug,
                universeId: action.universeId,
                fastIdeas: signal<(IdeaDTO<ScannerPlanDTO> | IdeaDTO<SearchPlanDTO>)[]>([]),
                symbolFilter: signal<string | null>(action.symbolFilter),
                symbolRetention: signal<Duration>(action.symbolRetention),
            };
        case 'UPDATE_FAST_IDEAS':
            if (state.type === 'initializing') {
                return state;
            }

            // Check if ideas have changed
            const currentIdeas = state.fastIdeas.value;
            const newIdeaIds = action.ideas.map((idea) => idea.idea.id);
            const currentIdeaIds = currentIdeas.map((idea) => idea.idea.id);

            const ideasChanged =
                newIdeaIds.length !== currentIdeaIds.length || !newIdeaIds.every((id) => currentIdeaIds.includes(id));

            // Update the ideas value
            state.fastIdeas.value = action.ideas;

            // If ideas changed and we were in ready state, go back to waiting for symbols
            if (ideasChanged && state.type === 'ready') {
                return {
                    type: 'waiting-for-symbols',
                    instanceId: v4(),
                    changeCloseSlug: state.changeCloseSlug,
                    symbolSlug: state.symbolSlug,
                    universeId: state.universeId,
                    fastIdeas: state.fastIdeas,
                    symbolFilter: state.symbolFilter,
                    symbolRetention: state.symbolRetention,
                };
            }

            return state;
        case 'UPDATE_SLOW_IDEAS':
            if (state.type === 'initializing') {
                return state;
            }

            // Check for universe ID changes
            const slowIdeasWithUniverse = action.ideas.filter((idea) => 'universeId' in idea);
            const newUniverse = slowIdeasWithUniverse.length > 0 ? slowIdeasWithUniverse[0].universeId : null;

            // If we're in ready state and universe changed, go back to waiting for symbols
            if (state.type === 'ready' && state.universeId.value !== newUniverse) {
                return {
                    type: 'waiting-for-symbols',
                    instanceId: v4(),
                    changeCloseSlug: state.changeCloseSlug,
                    symbolSlug: state.symbolSlug,
                    universeId: state.universeId,
                    fastIdeas: state.fastIdeas,
                    symbolFilter: state.symbolFilter,
                    symbolRetention: state.symbolRetention,
                };
            }

            return state;
        case 'UPDATE_SYMBOL_FILTER':
            if (state.type !== 'initializing') {
                state.symbolFilter.value = action.symbol;
            }

            return state;
        case 'UPDATE_SYMBOL_RETENTION':
            if (state.type !== 'initializing') state.symbolRetention.value = action.duration;
            return state;
        case 'SYMBOLS_SNAPSHOT_RECEIVED':
            if (state.type === 'initializing') return state;
            if (state.type === 'ready') {
                state.symbols.value = new Set(action.symbols);
                return state;
            }
            return { ...state, type: 'ready', symbols: signal(new Set(action.symbols)) };
        case 'SYMBOLS_UPDATE_RECEIVED': {
            if (state.type !== 'ready') return state;
            const symbols = new Set(state.symbols.value);
            for (const symbol of action.removed) symbols.delete(symbol);
            for (const symbol of action.added) symbols.add(symbol);
            state.symbols.value = symbols;
            return state;
        }
        case 'RESET':
            return { type: 'initializing' };
        default:
            return state;
    }
}

@injectable()
export class NewsWidgetModelImpl extends ReactiveInjectable implements NewsWidgetModel {
    readonly gridColumns: TimeListColumns;
    readonly newsList: NewsTimeList;
    readonly showRetirementTime = signal(false);
    #adapter: NewsWidgetAdapter;
    #queryClient: ReactBindings['QueryClient'];
    #formulaService: ReactBindings['FormulaService'];
    #table: ReactBindings['TableModel'];
    #defaultUniverseServiceProvider: ReactBindings['DefaultUniverseServiceProvider'];
    #slugMapService: ReactBindings['SlugMapService'];
    #slugMapServiceProvider: ReactBindings['SlugMapServiceProvider'];
    #channelSlice: ChannelSlice;
    #widgetTabSlice: WidgetTabSlice;

    #allowedInstrumentTypes: ReadonlySignal<InstrumentType[]> = signal(['equity']);

    // New header system signals
    #connectionStatus: Signal<
        ConnectionStatusSignal['widget::connectionStatus'] extends ReadonlySignal<infer T> ? T : never
    > = signal({
        status: 'uninitialized' as TableStatus,
        progress: undefined,
        failureCount: 0,
    });

    headerElements!: WidgetHeaderElement[];

    eventBus!: NewsWidgetModel['eventBus'];

    constructor(
        @inject('NewsWidgetAdapter') adapter: NewsWidgetAdapter,
        @inject('Store') store: ReactBindings['Store'],
        @inject('Logger') logger: Logger,
        @inject('DefaultUniverseServiceProvider')
        defaultUniverseServiceProvider: ReactBindings['DefaultUniverseServiceProvider'],
        @inject('SlugMapServiceProvider') slugMapServiceProvider: ReactBindings['SlugMapServiceProvider'],
        @inject('SlugMapService') slugMapService: ReactBindings['SlugMapService'],
        @inject('TableModel') table: ReactBindings['TableModel'],
        @inject('QueryClient') queryClient: ReactBindings['QueryClient'],
        @inject('FormulaService') formulaService: ReactBindings['FormulaService'],
        @inject('ChannelSlice') channelSlice: ChannelSlice,
        @inject('WidgetTabSlice') widgetTabSlice: WidgetTabSlice,
        @inject('WorkspaceService') @optional() workspaceService?: WorkspaceService,
        @inject('TabId') @optional() tabId?: string,
    ) {
        // eslint-disable-next-line prefer-rest-params -- forwarding constructor args
        super(...arguments);

        this.#adapter = adapter;
        this.gridColumns = this.disposableStack.use(new TimeListColumns(newsGridColumns, adapter));
        this.#queryClient = queryClient;
        this.newsList = this.disposableStack.use(createNewsList(queryClient));
        const updateDebug = () => {
            this.showRetirementTime.value = enableNewsListDebugging.select(store.getState());
        };
        updateDebug();
        this.disposableStack.defer(store.subscribe(updateDebug));
        this.#formulaService = formulaService;
        this.#table = table;
        this.#defaultUniverseServiceProvider = defaultUniverseServiceProvider;
        this.#slugMapService = slugMapService;
        this.#slugMapServiceProvider = slugMapServiceProvider;
        this.#channelSlice = channelSlice;
        this.#widgetTabSlice = widgetTabSlice;

        this.#state = { type: 'initializing' };

        this.#logger = logger.getSubLogger({ name: 'NewsWidgetModel' });
        if (TRACE) {
            this.disposableStack.defer(
                effect(() => {
                    const state = this.newsList.state.value;
                    this.#logger.trace('News list scheduling state', {
                        generation: state.source.generation,
                        sourceStatus: state.source.status,
                        symbols: state.activeSymbols.length,
                        roundSymbols: state.roundSymbols.length,
                        pendingSymbols: state.pending.length,
                        viewportHeight: state.measurement.viewportHeight,
                        rows: state.rows.length,
                        fetchStatus: state.fetch.status,
                        disposed: state.disposed,
                    });
                }),
            );
        }

        if (tabId && workspaceService) {
            workspaceService.registerWidget(tabId, this);
            this.disposableStack.defer(() => workspaceService.deregisterWidget(tabId));
        }
    }

    get fastIdeas(): ReadonlySignal<(IdeaDTO<ScannerPlanDTO> | IdeaDTO<SearchPlanDTO>)[]> | null {
        if (this.#state.type !== 'initializing') {
            return this.#state.fastIdeas;
        }
        return null;
    }

    async init(tabId?: string) {
        const baseElements = tabId
            ? COMMON_HEADER_ELEMENTS
            : STANDALONE_COMMON_HEADER_ELEMENTS.filter((el) => el.type !== 'channelPicker');
        this.headerElements = [
            ...baseElements,
            // Left side (< 50)
            { type: 'symbolPicker', position: 25, priority: 80, placeholder: 'symbol' },
            { type: 'symbolRetention', position: 28, priority: 50 },
            { type: 'helpDocument', documentId: 'news-overview', position: 30, priority: 40 },
            // Right side (> 50, spacer is at 50)
            {
                type: 'ideasMenu',
                key: 'slow',
                position: 54,
                priority: 60,

                selectType: 'single',
                ideaType: 'slow',
                ideaPlanTypes: ['universe', 'screener', 'watchlist'],
                showSelected: false,
                allowDelete: false,
            },
            {
                type: 'ideasMenu',
                key: 'fast',
                position: 56,
                priority: 60,

                selectType: 'multi',
                ideaType: 'fast',
                ideaPlanTypes: ['scanner', 'if-then'],
                showSelected: true,
                allowDelete: false,
            },
            { type: 'connectionStatus', position: 995, priority: 995 },
        ];

        this.#devtools = connectReducerDevtools({ name: `News [${tabId ?? 'standalone'}]` });
        if (this.#devtools) {
            this.disposableStack.use(this.#devtools);
            this.#devtools.init(this.#state);
        }

        if (tabId) {
            // Initialize slices for the new widget header system (workspace only)
            this.#channelSlice.init(tabId);
            this.#widgetTabSlice.init(tabId);
            this.disposableStack.use(this.#channelSlice);
            this.disposableStack.use(this.#widgetTabSlice);
        }

        // Build the EventBus (must be after slice init so slices provide signals in workspace mode)
        this.eventBus = this.#buildEventBus(tabId);

        const adapterState = this.#adapter.getState();

        // The universeId field stores either a universe ID or a screener/watchlist idea ID.
        // On init, probe the stored value: if it resolves as an idea DTO it's a screener/watchlist;
        // otherwise treat it as a universe ID.
        const storedId = adapterState.universeId;
        if (storedId) {
            try {
                const dto = await this.#queryClient.fetchQuery(
                    getIdeaDTOByIdQuery(this.#queryClient, this.#formulaService, this.#slugMapService, storedId),
                );
                if (TRACE) {
                    this.#logger.trace('Initializing with slow idea from adapter', { ideaId: storedId });
                }
                this.#slowIdeaSourceSignal.value = {
                    type: 'idea',
                    id: storedId,
                    dto: dto as IdeaDTO<ScreenerPlanDTO | WatchlistPlanDTO>,
                };
            } catch {
                if (TRACE) {
                    this.#logger.trace('Initializing with universe from adapter', { universeId: storedId });
                }
                this.#slowIdeaSourceSignal.value = { type: 'universe', id: storedId };
            }
        } else {
            // No stored ID — initialize with the default universe
            const defaultUniverseService = await this.#defaultUniverseServiceProvider();
            const defaultUniverseId = defaultUniverseService.universe.value.id;
            if (TRACE) {
                this.#logger.trace('Initializing with default universe', { defaultUniverseId });
            }
            this.#adapter.persist({ universeId: defaultUniverseId });
            this.#slowIdeaSourceSignal.value = { type: 'universe', id: defaultUniverseId };
        }
        this.disposableStack.defer(
            this.#adapter.subscribe((nextAdapterState: NewsPersistedState) => {
                const newStoredId = nextAdapterState.universeId;
                const currentId = this.#slowIdeaSourceSignal.value?.id ?? null;

                if (newStoredId !== currentId) {
                    // Treat adapter-driven changes as universe selections (ideas are set in-memory first)
                    this.#slowIdeaSourceSignal.value = newStoredId ? { type: 'universe', id: newStoredId } : null;
                    if (TRACE) {
                        this.#logger.trace({ message: 'User selected new universe', universeId: newStoredId });
                    }
                    if (this.#state.type !== 'initializing') {
                        this.#updateState({
                            type: 'UPDATE_SLOW_IDEAS',
                            ideas: newStoredId ? [{ universeId: newStoredId }] : [],
                        });
                    }
                }

                // Update symbolFilter in state from adapter
                if (this.#state.type !== 'initializing') {
                    // Only update if the values are actually different, treating null and empty string as equivalent
                    const currentValue = this.#state.symbolFilter.value;
                    const newValue = nextAdapterState.symbolFilter ?? null;

                    // Skip update if they're identical or if one is null and the other is empty string
                    if (
                        currentValue !== newValue &&
                        !((currentValue === null && newValue === '') || (currentValue === '' && newValue === null))
                    ) {
                        this.#updateState({ type: 'UPDATE_SYMBOL_FILTER', symbol: nextAdapterState.symbolFilter });
                    }
                }

                // Update symbolRetention in state from adapter
                if (this.#state.type !== 'initializing') {
                    const adapterRetention = nextAdapterState.symbolRetention;
                    const reduxDuration = adapterRetention
                        ? Duration.parse(adapterRetention)
                        : DEFAULT_SYMBOL_RETENTION;
                    const currentDuration = this.#state.symbolRetention.value;
                    if (!reduxDuration.equals(currentDuration)) {
                        this.#updateState({ type: 'UPDATE_SYMBOL_RETENTION', duration: reduxDuration });
                    }
                }

                void this.#syncFastIdeasFromAdapter(nextAdapterState.fastIdeasIds).catch((err) => {
                    this.#logger.error('Failed to sync fast ideas from adapter', {
                        error: err instanceof Error ? err.message : err,
                    });
                });
            }),
        );

        const slugMapService = await this.#slugMapServiceProvider();

        const symbolSlug = computed(() => slugMapService.slugMap.value[Slugs.symbol]);
        const changeCloseSlug = computed(() => slugMapService.slugMap.value[Slugs.symbolChangeClose]);

        // Hydrate symbolRetention from adapter, or use default if not set
        const adapterSymbolRetention = adapterState.symbolRetention;
        const initialSymbolRetention = adapterSymbolRetention
            ? Duration.parse(adapterSymbolRetention)
            : DEFAULT_SYMBOL_RETENTION;

        // Hydrate symbolFilter from adapter
        const initialSymbolFilter = adapterState.symbolFilter ?? null;

        this.#updateState({
            type: 'INITIALIZE_SUCCESS',
            changeCloseSlug,
            symbolSlug,
            universeId: this.#universeId,
            symbolFilter: initialSymbolFilter,
            symbolRetention: initialSymbolRetention,
        });

        await this.#syncFastIdeasFromAdapter(adapterState.fastIdeasIds);

        // Initialize query options signal once in constructor
        const queryOptions: ReadonlySignal<QueryObserverOptions<FetchableTableCreationResultWithColumns>> = computed(
            () => {
                const symbolFilterValue = this.symbolFilter?.value ?? null;
                const source = this.#slowIdeaSourceSignal.value;
                const fastIdeasValue = this.#state.type !== 'initializing' ? this.#state.fastIdeas.value : [];
                return {
                    queryKey: [
                        'news-widget',
                        'table',
                        symbolFilterValue,
                        source?.type ?? null,
                        source?.id ?? null,
                        fastIdeasValue.map((idea) => idea.idea.id),
                    ],
                    queryFn: async (_context, debugIdentifier?: string) => {
                        let stage: Stage;
                        if (symbolFilterValue) {
                            stage = SourceStage.allForKey('ticker');
                        } else if (source?.type === 'idea') {
                            const allStage = SourceStage.allForKey('ticker');
                            stage = ideaDTOToStage(allStage, source.dto);
                        } else if (!source) {
                            stage = SourceStage.allForKey('ticker');
                        } else {
                            stage = SourceStage.fromTickerUniverseId(source.id);
                        }

                        if (symbolFilterValue) {
                            stage = FilterStage.fromInputStage(stage, {
                                type: 'filter',
                                formula: `symbol == "${symbolFilterValue}"`,
                                imports: [{ ...symbolSlug.value, alias: 'symbol' }],
                            });
                        } else {
                            if (fastIdeasValue.length > 1) {
                                const stages = fastIdeasValue.map((idea) => ideaDTOToStage(stage, idea));

                                if (stages.length > 0) {
                                    stage = UnionStage.from(stages);
                                }
                            } else if (fastIdeasValue.length === 1) {
                                const idea = fastIdeasValue[0];
                                stage = ideaDTOToStage(stage, idea);
                            }
                        }

                        const formula = 'changeClose > 0 ? 1 : changeClose < 0 ? -1 : 0';
                        const imports = [{ ...changeCloseSlug.value, alias: 'changeClose' }];

                        stage = OutputStage.fromInputStage(stage, {}, [
                            { id: 'symbol', type: 'indicator', indicator: symbolSlug.value },
                            { id: 'changeDirection', type: 'formula', formula, imports },
                        ]);

                        return {
                            ...(await createTable({ leafStage: stage.build() }, undefined, debugIdentifier)),
                            fetchMode: undefined,
                        };
                    },
                    enabled: true,
                    staleTime: 0,
                    gcTime: 0,
                    refetchOnWindowFocus: false,
                };
            },
        );

        // Invalidate old requests before publishing replacement table options.
        const tableOptions = signal(queryOptions.peek());
        let sourceKey: string | undefined;
        this.disposableStack.defer(
            effect(() => {
                const options = queryOptions.value;
                const nextKey = hashKey(options.queryKey!);
                if (nextKey !== sourceKey) {
                    sourceKey = nextKey;
                    this.newsList.beginSource({
                        sourceId: nextKey,
                        selectionKind: this.symbolFilter?.peek() ? 'single-symbol' : 'multi-symbol',
                        mode: 'live',
                        query: { anchor: null },
                        retention: this.symbolRetention?.peek().toMillis() ?? 0,
                    });
                }
                tableOptions.value = options;
            }),
        );
        this.disposableStack.defer(
            effect(() => {
                this.newsList.setRetention(this.symbolRetention?.value.toMillis() ?? 0);
            }),
        );
        this.#table.init({
            initBounds: { firstRow: 0, windowSize: 100000 },
            initThrottle: 1000,
            queryOptions: tableOptions,
            devtoolsName: 'News/table',
        });

        // A complete initial selection may arrive as a snapshot or a diff with a full row-ID image.
        this.disposableStack.use(
            this.#table.updatesWithReset$.subscribe((updates) => {
                if (!updates) return;
                if (TRACE) {
                    this.#logger.trace('News membership batch received', {
                        kind: updates === NEW_TABLE_RESET ? 'reset' : updates.type,
                        sourceStatus: this.newsList.state.peek().source.status,
                        tableKey: this.#table.key,
                    });
                }

                if (updates === NEW_TABLE_RESET) {
                    this.newsList.membershipReset();
                    this.#rowIdToSymbolMap.clear();
                    this.#unresolvedSymbolRowIds.clear();
                    this.#symbolChangeDirectionsMap.value = new Map();
                    return;
                }

                if (this.newsList.state.peek().source.status === 'awaiting-table-reset') return;
                if (updates.type === 'quote' && this.newsList.state.peek().source.status === 'awaiting-symbols') {
                    return;
                }

                if (updates.type === 'snapshot') {
                    this.#rowIdToSymbolMap.clear();
                    this.#unresolvedSymbolRowIds.clear();
                    const newSymbols = new Set<string>();
                    // Create a new Map to avoid modifying the one in the signal
                    const changeDirections = new Map<string, number>();

                    for (const update of updates) {
                        this.#rowIdToSymbolMap.set(update[RowIdField]!, update.symbol);
                        newSymbols.add(update.symbol);

                        // Get change direction directly from the changeDirection field
                        const changeDirection = Number(update['changeDirection']);
                        if (!isNaN(changeDirection)) {
                            changeDirections.set(update.symbol, changeDirection);
                        }
                    }

                    // Update the change directions signal
                    this.#symbolChangeDirectionsMap.value = changeDirections;
                    if (this.newsList.state.peek().source.status === 'awaiting-symbols') {
                        this.newsList.activateSymbols([...newSymbols]);
                    } else this.newsList.publishSymbols([...newSymbols]);

                    this.#updateState({
                        type: 'SYMBOLS_SNAPSHOT_RECEIVED',
                        symbols: newSymbols,
                    });
                    return;
                } else if (updates.type === 'diff') {
                    const previousSymbols = new Set(this.newsList.state.peek().activeSymbols);
                    const changeDirections = new Map(this.#symbolChangeDirectionsMap.peek());
                    for (const rowId of updates.deletedRowIds) {
                        this.#rowIdToSymbolMap.delete(rowId);
                        this.#unresolvedSymbolRowIds.delete(rowId);
                    }
                    for (const rowId of updates.orderedRowIds) {
                        if (!this.#rowIdToSymbolMap.has(rowId)) this.#unresolvedSymbolRowIds.add(rowId);
                    }
                    for (const rowId of updates.rowIdToRowIndex.keys()) {
                        if (!this.#rowIdToSymbolMap.has(rowId)) this.#unresolvedSymbolRowIds.add(rowId);
                    }
                    for (const update of updates) {
                        const rowId = update[RowIdField]!;
                        const symbol = update.symbol;
                        if (!symbol) {
                            if (!this.#rowIdToSymbolMap.has(rowId)) this.#unresolvedSymbolRowIds.add(rowId);
                            continue;
                        }
                        this.#rowIdToSymbolMap.set(rowId, symbol);
                        this.#unresolvedSymbolRowIds.delete(rowId);
                        const direction = Number(update.changeDirection);
                        if (!isNaN(direction)) changeDirections.set(symbol, direction);
                    }
                    // Only explicit deletions remove membership. Missing ordering entries are not removals.
                    // Keep unresolved IDs across batches until their values arrive or they are deleted.
                    const symbols = new Set(this.#rowIdToSymbolMap.values());
                    for (const symbol of changeDirections.keys()) {
                        if (!symbols.has(symbol)) changeDirections.delete(symbol);
                    }
                    this.#symbolChangeDirectionsMap.value = changeDirections;
                    if (this.#unresolvedSymbolRowIds.size > 0) {
                        if (TRACE) {
                            this.#logger.trace('News membership awaiting complete symbol values', {
                                unresolvedRows: this.#unresolvedSymbolRowIds.size,
                                knownRows: this.#rowIdToSymbolMap.size,
                            });
                        }
                        return;
                    }
                    if (this.newsList.state.peek().source.status === 'awaiting-symbols') {
                        this.newsList.activateSymbols([...symbols]);
                        this.#updateState({ type: 'SYMBOLS_SNAPSHOT_RECEIVED', symbols });
                        return;
                    }
                    // Compare complete memberships, preserving delete/re-add and duplicate symbol rows.
                    const added = symbols.difference(previousSymbols);
                    const removed = previousSymbols.difference(symbols);
                    this.newsList.updateSymbols([...added], [...removed]);
                    this.#updateState({ type: 'SYMBOLS_UPDATE_RECEIVED', added, removed });
                } else if (updates.type === 'quote') {
                    // Create a new Map from the current change directions
                    const changeDirections = new Map(this.#symbolChangeDirectionsMap.value);

                    // Handle quote updates
                    for (const update of updates) {
                        const symbol = update.symbol;
                        if (symbol) {
                            // Get change direction directly from the changeDirection field
                            const changeDirection = Number(update['changeDirection']);
                            if (!isNaN(changeDirection)) {
                                changeDirections.set(symbol, changeDirection);
                            }
                        }
                    }

                    // Update the change directions signal
                    this.#symbolChangeDirectionsMap.value = changeDirections;
                }
            }),
        );

        // Sync connection status for new header system
        this.disposableStack.defer(
            effect(() => {
                const status = this.#table.connectionStatus.value;
                const progress = this.#table.progress.value;
                const failureCount = this.#table.failureCount.value;
                this.#connectionStatus.value = {
                    status,
                    progress,
                    failureCount,
                };
            }),
        );
    }

    #logger: Logger;

    /**
     * Builds the EventBus for the new widget header system.
     * Combines widget-specific signals/events with slice-provided ones.
     */
    #buildEventBus(tabId?: string): NewsWidgetModel['eventBus'] {
        let merged = MergedEventBus.from(
            EventBusBuilder.create()
                // News-specific signals
                .addSignal('widget::instrument', () =>
                    computed(() => {
                        const state = this.#stateSignal.value;
                        if (state.type === 'initializing') return '';
                        return state.symbolFilter.value ?? '';
                    }),
                )
                .addSignal('widget::allowedInstrumentTypes', () => this.#allowedInstrumentTypes)
                .addSignal('widget::symbolRetention', () =>
                    computed(() => {
                        const state = this.#stateSignal.value;
                        if (state.type === 'initializing') return Duration.ofSeconds(0);
                        return state.symbolRetention.value;
                    }),
                )
                .addSignal('widget::connectionStatus', () => this.#connectionStatus)
                .addSignal('widget::slowIdea', () =>
                    computed(() => {
                        return this.#slowIdeaDTO.value;
                    }),
                )
                .addSignal('widget::selectedFastIdeas', () =>
                    computed(() => {
                        const state = this.#stateSignal.value;
                        if (state.type === 'initializing') return [];
                        return state.fastIdeas.value.flatMap((idea) => {
                            const id = idea.idea.id;
                            return id !== undefined ? [{ type: 'idea' as const, id }] : [];
                        });
                    }),
                )
                .addSignal('widget::selectedSlowIdeas', () =>
                    computed(() => {
                        const source = this.#slowIdeaSourceSignal.value;
                        if (!source) return [];
                        return [{ type: source.type, id: source.id }];
                    }),
                )
                // News-specific events
                .addEvent('widget::instrumentChange', (payload) => {
                    this.onChangeSymbolFilter(payload.instrument);
                })
                .addEvent('widget::updateSymbolRetention', (payload) => {
                    this.onChangeSymbolRetention(Duration.parse(payload.duration));
                })
                .addEvent('widget::onIdeasSelect', (payload) => {
                    // This event is only emitted for fast ideas by the IdeasMenu component
                    // (slow ideas use widget::selectSlowIdea instead)
                    const fastIdeas = payload.ideas.filter((idea) => !('universeId' in idea)) as (
                        | IdeaDTO<ScannerPlanDTO>
                        | IdeaDTO<SearchPlanDTO>
                    )[];
                    // Always forward to handler, even empty array (to clear selection)
                    this.onSelectFastIdeas(fastIdeas);
                })
                .addEvent('widget::onIdeaDelete', () => {
                    // News widget doesn't support idea deletion
                })
                .addEvent('widget::selectSlowIdea', (payload) => {
                    // Type assertion needed because event uses generic IdeaDTO while method expects specific plan types
                    this.onSelectSlowIdeas(payload.ideas as Parameters<typeof this.onSelectSlowIdeas>[0]);
                })
                .addEvent('widget::connectionStatusRefetch', () => {
                    this.refetch();
                })
                .build(),
        );

        if (tabId) {
            // Workspace mode: merge slice event buses (they provide widget::name, channel signals, etc.)
            merged = merged.merge(this.#channelSlice.eventBus).merge(this.#widgetTabSlice.eventBus);
        } else {
            // Standalone mode: provide a static name signal since slices aren't initialized
            merged = merged.merge(
                EventBusBuilder.create()
                    .addSignal('widget::name', () => signal('News'))
                    .build(),
            );
        }

        return merged as NewsWidgetModelEventBus;
    }

    #rowIdToSymbolMap = new Map<number, string>();
    #unresolvedSymbolRowIds = new Set<number>();
    #symbolChangeDirectionsMap = signal<Map<string, number>>(new Map());

    get symbolChangeDirections(): ReadonlySignal<ReadonlyMap<string, number>> {
        return this.#symbolChangeDirectionsMap;
    }

    private isUniverseIdFormat(idea: any): idea is { universeId: string } {
        return idea && typeof idea === 'object' && 'universeId' in idea && typeof idea.universeId === 'string';
    }

    private isTypeIdFormat(idea: any): idea is { type: string; id: string } {
        return (
            idea &&
            typeof idea === 'object' &&
            'type' in idea &&
            idea.type === 'universe' &&
            'id' in idea &&
            typeof idea.id === 'string'
        );
    }

    onSelectSlowIdeas(
        ideas: (
            | IdeaDTO<ScreenerPlanDTO>
            | IdeaDTO<WatchlistPlanDTO>
            | { universeId: string }
            | { type: string; id: string }
        )[],
    ): void {
        if (TRACE) {
            this.#logger.trace('onSelectSlowIdeas received ideas', {
                ideas: JSON.stringify(ideas),
                currentSource: this.#slowIdeaSourceSignal.value,
            });
        }

        const firstIdea = ideas[0];

        if (!firstIdea) {
            // Clear selection
            this.#slowIdeaSourceSignal.value = null;
            this.#adapter.persist({ universeId: null });
            return;
        }

        // Check for IdeaDTO (screener or watchlist) — has an 'idea' property
        if ('idea' in firstIdea) {
            const ideaDTO = firstIdea as IdeaDTO<ScreenerPlanDTO | WatchlistPlanDTO>;
            const ideaId = ideaDTO.idea.id ?? null;
            if (TRACE) {
                this.#logger.trace('Selected screener/watchlist slow idea', { ideaId });
            }
            // Set in-memory signal first so the adapter subscription can detect it's an idea
            this.#slowIdeaSourceSignal.value = ideaId ? { type: 'idea', id: ideaId, dto: ideaDTO } : null;
            // Store the idea ID in the universeId field (single-field paradigm)
            if (ideaId !== this.#adapter.getState().universeId) {
                this.#adapter.persist({ universeId: ideaId });
            }
            return;
        }

        let selectedUniverse: string | null = null;

        const ideaWithUniverseId = ideas.find((idea) => this.isUniverseIdFormat(idea));
        if (ideaWithUniverseId) {
            selectedUniverse = ideaWithUniverseId.universeId;
        } else {
            const ideaWithTypeUniverse = ideas.find((idea) => this.isTypeIdFormat(idea));
            if (ideaWithTypeUniverse) {
                selectedUniverse = ideaWithTypeUniverse.id;
            }
        }

        if (TRACE) {
            this.#logger.trace('Selected universe', {
                selectedUniverse,
                currentSource: this.#slowIdeaSourceSignal.value,
            });
        }

        const currentId = this.#slowIdeaSourceSignal.value?.id ?? null;
        if (currentId !== selectedUniverse) {
            this.#slowIdeaSourceSignal.value = selectedUniverse ? { type: 'universe', id: selectedUniverse } : null;
            this.#adapter.persist({ universeId: selectedUniverse });
        }
    }

    onSelectFastIdeas(ideas: (IdeaDTO<ScannerPlanDTO> | IdeaDTO<SearchPlanDTO>)[]): void {
        this.#updateState({ type: 'UPDATE_FAST_IDEAS', ideas });
        const ideaIds = ideas.map((idea) => idea.idea.id).filter((id): id is string => id !== undefined);
        this.#adapter.persist({ fastIdeasIds: ideaIds });
    }

    async #syncFastIdeasFromAdapter(fastIdeaIds: string[]): Promise<void> {
        if (this.#state.type === 'initializing') {
            return;
        }

        const currentIdeaIds = this.#state.fastIdeas.value
            .map((idea) => idea.idea.id)
            .filter((id): id is string => id !== undefined);

        if (
            fastIdeaIds.length === currentIdeaIds.length &&
            fastIdeaIds.every((ideaId, index) => ideaId === currentIdeaIds[index])
        ) {
            return;
        }

        const ideaResults = await Promise.all(
            fastIdeaIds.map((id) =>
                this.#queryClient
                    .fetchQuery(getIdeaDTOByIdQuery(this.#queryClient, this.#formulaService, this.#slugMapService, id))
                    .catch(() => null),
            ),
        );

        const validIdeas = ideaResults
            .filter(notNullOrUndefined)
            .filter(
                (idea): idea is IdeaDTO<ScannerPlanDTO> | IdeaDTO<SearchPlanDTO> =>
                    idea.plan instanceof ScannerPlanDTO || idea.plan instanceof SearchPlanDTO,
            );

        this.#updateState({ type: 'UPDATE_FAST_IDEAS', ideas: validIdeas });
    }

    onSelectSymbol(symbol: string) {
        this.#adapter.onSelectSymbol(symbol);
    }

    openChartForSymbol(symbol: string) {
        this.#adapter.openChartForSymbol(symbol);
    }

    openLensForSymbol(symbol: string) {
        this.#adapter.openLensForSymbol(symbol);
    }

    #resetState() {
        if (this.#state.type === 'initializing') {
            return;
        }

        this.#adapter.persist({ symbolFilter: null });

        this.#updateState({ type: 'RESET' });
    }

    #state: NewsWidgetModelState;
    #stateSignal = signal<NewsWidgetModelState>({ type: 'initializing' });
    #devtools: ReducerDevtoolsConnection<NewsWidgetModelState, NewsWidgetModelAction> | undefined;

    // Keeping this @reacts setter for backward compatibility, transition to using #stateSignal directly
    @reacts private set state(newState: NewsWidgetModelState) {
        this.#state = newState;
        this.#stateSignal.value = newState;
    }

    #updateState(action: NewsWidgetModelAction) {
        const prevState = this.#state;
        if (TRACE) {
            this.#logger.trace('Dispatching action', { type: action.type });
        }
        const newState = newsWidgetModelReducer(this.#state, action);

        if (prevState !== newState) {
            if (TRACE) {
                this.#logger.trace('State updated', { action, prevState, state: this.#state });
            }

            this.state = newState;
            this.#devtools?.send(action, this.#state);

            // Keep slowIdeaSourceSignal in sync with adapter — but only when no slow idea is active,
            // since the universeId field also stores screener/watchlist idea IDs.
            if (
                this.#slowIdeaSourceSignal.value?.type !== 'idea' &&
                this.#adapter.getState().universeId !== (this.#slowIdeaSourceSignal.value?.id ?? null)
            ) {
                const adapterUniverseId = this.#adapter.getState().universeId;
                this.#slowIdeaSourceSignal.value = adapterUniverseId
                    ? { type: 'universe', id: adapterUniverseId }
                    : null;
            }

            if (newState.type === 'initializing') {
                this.#viewDetailsSignal.value = { type: 'loading' };
            } else if (newState.type === 'waiting-for-symbols') {
                this.#viewDetailsSignal.value = {
                    type: 'waiting-for-symbols',
                };
            } else {
                this.#viewDetailsSignal.value = {
                    type: 'ready',
                    instanceId: newState.instanceId,
                    symbols: newState.symbols,
                    symbolFilter: newState.symbolFilter,
                    fastIdeas: newState.fastIdeas,
                };
            }
        }
    }

    get symbolFilter(): ReadonlySignal<string | null> | null {
        if (this.#state.type !== 'initializing') {
            return this.#state.symbolFilter;
        }
        return null;
    }

    get symbolRetention(): ReadonlySignal<Duration> | null {
        if (this.#state.type !== 'initializing') {
            return this.#state.symbolRetention;
        }
        return null;
    }

    // Method to change the symbol filter
    onChangeSymbolFilter(value: string) {
        // Don't dispatch if the value is already the same to prevent loops
        // This is especially important for empty string vs null comparisons
        const currentValue = this.#state.type !== 'initializing' ? this.#state.symbolFilter.value : null;
        if ((value === '' && currentValue === null) || value === currentValue) {
            return;
        }
        this.#adapter.persist({ symbolFilter: value === '' ? null : value });
    }

    // Method to change the symbol retention duration
    onChangeSymbolRetention(duration: Duration) {
        this.#adapter.persist({ symbolRetention: duration.toString() });
    }

    get symbols(): ReadonlySignal<ReadonlySet<string>> | null {
        if (this.#state.type === 'ready') {
            return this.#state.symbols as ReadonlySignal<ReadonlySet<string>>;
        }
        return null;
    }

    get slowIdeaSource(): ReadonlySignal<SlowIdeaSource | null> {
        return this.#slowIdeaSourceSignal;
    }

    #slowIdeaSourceSignal = signal<SlowIdeaSource | null>(null);
    // Convenience computeds for internal consumption
    #universeId = computed(() => {
        const s = this.#slowIdeaSourceSignal.value;
        return s?.type === 'universe' ? s.id : null;
    });
    #slowIdeaDTO = computed(() => {
        const s = this.#slowIdeaSourceSignal.value;
        return s?.type === 'idea' ? s.dto : null;
    });

    #viewDetailsSignal = signal<NewsWidgetViewDetails>({ type: 'loading' });

    get viewDetails(): ReadonlySignal<NewsWidgetViewDetails> {
        return this.#viewDetailsSignal;
    }

    get connectionStatus(): ReadonlySignal<TableStatus> {
        return this.#table.connectionStatus;
    }

    get progress(): ReadonlySignal<TableConstructionProgress> {
        return this.#table.progress;
    }

    refetch(): void {
        this.#table.refetch();
    }
}
